import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  errorToast,
  getLocalStorageItem,
  successToast
} from '../../../../utils/helper';
import Loader from '../../../../component/common/Loader';
import Topbar from '../../../PulseSurvey/Topbar';
import Table1 from '../../../../component/common/Table1';
import Pagination from '../../../../component/common/Pagination/Pagination';
import SearchInput from '../../../../component/common/Input/SearchInput';
import SelectMenu from '../../../../component/common/SelectMenu';
import { CompanyApi } from '../../../../api/companyApi';
import { PER_PAGE } from '../../../../utils/constants';

const ENROLLMENT_STATUS = {
  1: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
  2: { label: 'Active', className: 'bg-green-100 text-green-800' },
  3: { label: 'Suspended', className: 'bg-orange-100 text-orange-800' },
  4: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
  5: { label: 'Completed', className: 'bg-blue-100 text-blue-800' }
};

const STATUS_FILTER = [
  { value: '', name: 'All Status' },
  { value: '1', name: 'Pending' },
  { value: '2', name: 'Active' },
  { value: '3', name: 'Suspended' },
  { value: '4', name: 'Cancelled' },
  { value: '5', name: 'Completed' }
];

const columns = [
  {
    title: 'Employee',
    key: 'userName',
    parent: 'element',
    sortable: false,
    type: 'text',
    align: 'left'
  },
  {
    title: 'Scheme Name',
    key: 'schemeName',
    parent: 'element',
    sortable: false,
    type: 'text',
    align: 'left'
  },
  {
    title: 'Sacrifice Amount',
    key: 'sacrificeAmount',
    parent: 'element',
    sortable: false,
    type: 'custom',
    align: 'left',
    render: (row) => (
      <div className="text-sm">
        <div className="font-medium">£{row.sacrificeAmount}/month</div>
        <div className="text-gray-500">{row.sacrificePercentage}% of salary</div>
      </div>
    )
  },
  {
    title: 'Tax Savings',
    key: 'savings',
    parent: 'element',
    sortable: false,
    type: 'custom',
    align: 'left',
    render: (row) => (
      <div className="text-sm">
        <div className="text-green-600 font-medium">£{row.monthlyTaxSavings + row.monthlyNISavings}/month</div>
        <div className="text-gray-500">£{row.projectedAnnualSavings}/year</div>
      </div>
    )
  },
  {
    title: 'Enrolled On',
    key: 'enrollmentDate',
    parent: 'element',
    sortable: false,
    type: 'date',
    align: 'left'
  },
  {
    title: 'Status',
    key: 'status',
    parent: 'element',
    sortable: false,
    type: 'badge',
    align: 'left'
  },
  {
    title: '',
    key: 'action',
    type: 'action',
    align: 'right',
    isView: true
  }
];

function EnrollmentsList() {
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));

  const [loader, setLoader] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(STATUS_FILTER[0]);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');

  useEffect(() => {
    fetchEnrollments(currentPage, selectedPerPage.value, searchTerm, selectedStatus.value);
  }, [currentPage, selectedPerPage]);

  useEffect(() => {
    if (searchTerm !== undefined) {
      const delayDebounceFn = setTimeout(() => {
        fetchEnrollments(1, selectedPerPage.value, searchTerm, selectedStatus.value);
      }, 800);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchEnrollments(1, selectedPerPage.value, searchTerm, selectedStatus.value);
  }, [selectedStatus]);

  const fetchEnrollments = async (page, limit, search, status) => {
    setLoader(true);
    try {
      const response = await CompanyApi.getSalarySacrificeEnrollments(
        page,
        limit,
        search,
        status,
        null // schemeId - show all
      );

      if (response?.status === 200) {
        const formattedEnrollments = (response?.data?.data || []).map(enrollment => ({
          ...enrollment,
          element: {
            ...enrollment,
            userName: enrollment.userId?.name || 'N/A',
            userEmail: enrollment.userId?.email || '',
            schemeName: enrollment.schemeId?.schemeName || 'N/A',
            sacrificeAmount: enrollment.sacrificeAmount,
            sacrificePercentage: enrollment.sacrificePercentage,
            monthlyTaxSavings: enrollment.monthlyTaxSavings,
            monthlyNISavings: enrollment.monthlyNISavings,
            projectedAnnualSavings: enrollment.projectedAnnualSavings,
            enrollmentDate: enrollment.enrollmentDate,
            status: {
              label: ENROLLMENT_STATUS[enrollment.status]?.label,
              className: ENROLLMENT_STATUS[enrollment.status]?.className
            }
          }
        }));
        setEnrollments(formattedEnrollments);
        setTotalCount(response?.data?.totalRecords || 0);
        setCurrentPage(page);
      } else {
        errorToast(response?.message || 'Failed to fetch enrollments');
        setEnrollments([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      errorToast('Failed to fetch enrollments');
      setEnrollments([]);
      setTotalCount(0);
    } finally {
      setLoader(false);
    }
  };

  const handlePerPage = (perPage) => {
    setSelectedPerPage(perPage);
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  const handleViewEnrollment = (enrollment) => {
    // For now, show details in a simple alert
    // You can create a modal component for better UX
    const details = `
Employee: ${enrollment.element.userName}
Email: ${enrollment.element.userEmail}
Scheme: ${enrollment.element.schemeName}
Sacrifice Amount: £${enrollment.sacrificeAmount}/month (${enrollment.sacrificePercentage}%)
Monthly Savings: £${enrollment.monthlyTaxSavings + enrollment.monthlyNISavings}
Annual Savings: £${enrollment.projectedAnnualSavings}
Status: ${ENROLLMENT_STATUS[enrollment.status].label}
    `;
    alert(details.trim());
  };

  const refreshTable = () => {
    fetchEnrollments(currentPage, selectedPerPage.value, searchTerm, selectedStatus.value);
  };

  return (
    <>
      {loader && <Loader />}
      <div className="relative lg:mb-[45px]">
        <Helmet>
          <meta charSet="utf-8" />
          <title>B2B | Salary Sacrifice Enrollments</title>
        </Helmet>

        <Topbar title={'Salary Sacrifice Enrollments'} infoBtn={false} />

        <section className="flex flex-col mt-6 text-black w-full sm:w-[80%] md:w-[60%] lg:w-[45%]">
          <p className="text-shoorah-lightBlack dark:text-white mt-3.5 text-base">
            Review and manage employee enrollments in salary sacrifice schemes. Approve requests,
            track savings, and handle cancellations.
          </p>
        </section>

        <div className="mt-6">
          <div className="sm:flex justify-between items-center mb-4">
            <div className="flex gap-3 flex-wrap">
              <div className="hidden sm:flex items-center">
                <span className="text-sm mr-2 whitespace-nowrap text-shoorah-lightBlack dark:text-white">
                  Per Page
                </span>
                <div className="w-[80px]">
                  <SelectMenu
                    menuList={PER_PAGE}
                    showLabel={false}
                    defaultSelected={selectedPerPage}
                    setSelectedMenu={handlePerPage}
                  />
                </div>
              </div>

              <div className="w-[180px]">
                <SelectMenu
                  menuList={STATUS_FILTER}
                  showLabel={false}
                  defaultSelected={selectedStatus}
                  setSelectedMenu={setSelectedStatus}
                />
              </div>

              <div className="self-center">
                <SearchInput
                  id="searchKey"
                  name="searchKey"
                  type="text"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by employee name..."
                />
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-4 shadow">
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Enrollments</p>
              <p className="text-2xl font-bold text-shoorah-primary dark:text-white">{totalCount}</p>
            </div>
            <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-4 shadow">
              <p className="text-sm text-gray-500 dark:text-gray-400">Active Enrollments</p>
              <p className="text-2xl font-bold text-green-600">
                {enrollments.filter(e => e.status === 2).length}
              </p>
            </div>
            <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-4 shadow">
              <p className="text-sm text-gray-500 dark:text-gray-400">Pending Approval</p>
              <p className="text-2xl font-bold text-yellow-600">
                {enrollments.filter(e => e.status === 1).length}
              </p>
            </div>
          </div>

          <div className="mt-4">
            <Table1
              columns={columns}
              data={enrollments}
              name={'salary_sacrifice_enrollments_table'}
              setSelectedRow={(enrollment) => handleViewEnrollment(enrollment)}
              bottomBorder={totalCount > selectedPerPage.value}
              loader={loader}
              refreshTable={refreshTable}
              contentType={1}
            />
          </div>

          <div>
            {enrollments.length > 0 && !loader ? (
              <Pagination
                currentPage={currentPage}
                totalCount={totalCount}
                pageSize={selectedPerPage?.value}
                onPageChange={handlePagination}
              />
            ) : (
              <span />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default EnrollmentsList;
